// generateStatement.js
import { parseLocalDate } from './dates';
// Builds and opens a print-ready patient statement in a new browser window.
// Unlike CMS form overlays, this generates the full document from scratch.
// Brand colors: #171363 (navy), #00aeef (blue), #05998d (teal)

const v = (val) => (val != null && val !== '' ? String(val) : '');

function fmtMoney(n) {
  const num = parseFloat(n);
  if (isNaN(num)) return '$0.00';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(dateStr, opts = { month: 'long', day: 'numeric', year: 'numeric' }) {
  if (!dateStr) return '—';
  try {
    // Append noon UTC to prevent timezone-shifted off-by-one on date-only strings
    const d = new Date(String(dateStr).slice(0, 10) + 'T12:00:00');
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', opts);
  } catch { return '—'; }
}

const fmtShort = (d) => fmtDate(d, { month: 'short', day: 'numeric', year: 'numeric' });

function addDays(dateStr, n) {
  const d = new Date(String(dateStr).slice(0, 10) + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function openPrintWindow(html) {
  const win = window.open('', '_blank', 'width=940,height=1200,scrollbars=yes,resizable=yes');
  if (!win) {
    alert('Pop-up blocked. Please allow pop-ups for this site and try again.');
    return null;
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  return win;
}

const STATEMENT_CSS = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  @page { size: 8.5in 11in; margin: 0.4in 0.5in; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: 9pt;
    color: #1a1a2e;
    background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* ── Toolbar (screen only) ── */
  #toolbar {
    position: fixed; top: 0; left: 0; right: 0; z-index: 9999;
    background: #f0f0f0; padding: 8px 16px;
    display: flex; align-items: center; gap: 8px;
    border-bottom: 2px solid #ccc; font-family: sans-serif;
  }
  .pbtn {
    background: #171363; color: #fff; border: none;
    padding: 6px 22px; font-size: 10pt; cursor: pointer; border-radius: 4px;
  }
  .pbtn:hover { background: #0e0c40; }
  .cbtn {
    background: #6c757d; color: #fff; border: none;
    padding: 6px 18px; font-size: 10pt; cursor: pointer; border-radius: 4px;
  }
  #page { padding-top: 50px; max-width: 7.5in; margin: 0 auto; }
  @media print { #toolbar { display: none !important; } #page { padding-top: 0; } }

  /* ── Bulk print: one statement per sheet ── */
  .statement-sheet { page-break-after: always; }
  .statement-sheet:last-child { page-break-after: auto; }
  @media screen {
    .statement-sheet { margin-bottom: 28px; padding-bottom: 28px; border-bottom: 2px dashed #ccc; }
    .statement-sheet:last-child { margin-bottom: 0; padding-bottom: 0; border-bottom: none; }
  }

  /* ── Header ── */
  .stmt-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding-bottom: 8px; border-bottom: 3px solid #171363; margin-bottom: 10px;
    margin-top: 0.3in;
  }
  .practice-name { font-size: 14pt; font-weight: 700; color: #171363; margin-bottom: 2px; }
  .practice-meta { font-size: 7.5pt; color: #555; line-height: 1.5; }
  .stmt-title-block { text-align: right; }
  .stmt-title { font-size: 15pt; font-weight: 800; color: #00aeef; letter-spacing: -0.5px; }
  .stmt-meta { font-size: 7.5pt; color: #666; margin-top: 3px; line-height: 1.5; }

  /* ── Info grid ── */
  .info-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 10px;
  }
  .info-box {
    border: 1px solid #dde3ed; border-radius: 5px; padding: 7px 10px;
    background: #fafbfc;
  }
  .info-box-title {
    font-size: 6.5pt; text-transform: uppercase; letter-spacing: 0.8px;
    color: #00aeef; font-weight: 700; margin-bottom: 5px;
  }
  .info-row-line {
    display: flex; justify-content: space-between; font-size: 8pt; margin-bottom: 2px;
    gap: 3px;
  }
  .info-lbl { color: #777; white-space: nowrap; }
  .info-val { font-weight: 600; color: #1a1a2e; text-align: right; }

  /* ── Amount due ── */
  .amount-due-bar {
    background: #171363; color: #fff; border-radius: 6px;
    padding: 8px 14px; display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 12px;
  }
  .amount-due-left {}
  .amount-due-label { font-size: 9.5pt; font-weight: 700; }
  .amount-due-sub { font-size: 7pt; color: rgba(255,255,255,0.72); margin-top: 1px; }
  .amount-due-value { font-size: 18pt; font-weight: 800; color: #00aeef; }

  /* ── Section heading ── */
  .section-heading {
    font-size: 7.5pt; text-transform: uppercase; letter-spacing: 1.2px;
    font-weight: 700; color: #171363;
    border-bottom: 2px solid #00aeef; padding-bottom: 3px; margin-bottom: 8px;
  }

  /* ── Claim section ── */
  .claim-block { margin-bottom: 10px; }
  .claim-block-header {
    display: flex; align-items: center; gap: 9px;
    background: #eef2f8; border-left: 3px solid #00aeef;
    border-radius: 4px 4px 0 0; padding: 5px 10px;
  }
  .claim-badge {
    font-size: 7.5pt; font-weight: 700; color: #171363;
    background: #d8e3f0; padding: 2px 7px; border-radius: 3px; white-space: nowrap;
  }
  .claim-hdr-date { font-size: 8pt; color: #444; }
  .claim-hdr-provider { font-size: 7.5pt; color: #666; margin-left: auto; font-style: italic; }

  /* ── Detail table ── */
  .detail-table { width: 100%; border-collapse: collapse; font-size: 7pt; table-layout: fixed; }
  .detail-table thead tr { background: #f4f6fa; }
  .detail-table th {
    padding: 4px 5px; text-align: left; font-size: 6pt;
    text-transform: uppercase; letter-spacing: 0.4px; color: #777;
    border-bottom: 1px solid #dde3ed; word-break: break-word;
  }
  .detail-table th.r, .detail-table td.r { text-align: right; }
  .detail-table th.c, .detail-table td.c { text-align: center; }
  .detail-table td {
    padding: 4px 5px; border-bottom: 1px solid #f0f2f6; vertical-align: top;
    color: #2d2d4e; word-break: break-word; overflow-wrap: break-word;
  }
  .detail-table tbody:last-of-type tr:last-child td { border-bottom: none; }
  /* One tbody per claim: keep a claim on one page, and separate consecutive
     claims with a rule now that each no longer carries its own header. */
  .claim-group { page-break-inside: avoid; break-inside: avoid; }
  .claim-group + .claim-group td { border-top: 1px solid #c8d4e8; }
  .detail-table thead { display: table-header-group; }
  .cpt-pill {
    display: inline-block; background: #eef2f8; color: #171363;
    border-radius: 3px; padding: 1px 4px; font-size: 6.5pt; font-weight: 600;
  }
  .dim-cell { color: #bbb; }

  /* ── Claim totals row ── */
  .claim-totals-row td {
    background: #eef2f8; font-weight: 600; font-size: 7pt;
    border-top: 1px solid #c8d4e8 !important;
    border-bottom: 2px solid #c8d4e8 !important;
  }
  .claim-totals-label { color: #555; font-style: italic; font-weight: 500; }
  .balance-val { color: #171363; font-weight: 700; }
  .billed-to-val { color: #05998d; font-weight: 600; }

  /* ── Statement totals ── */
  .totals-wrapper { display: flex; justify-content: flex-end; margin: 2px 0 10px; }
  .totals-box {
    width: 260px; border: 1px solid #dde3ed; border-radius: 5px; overflow: hidden;
  }
  .totals-box-title {
    background: #171363; color: #fff; font-size: 7pt; font-weight: 700;
    padding: 5px 10px; text-transform: uppercase; letter-spacing: 0.6px;
  }
  .totals-line {
    display: flex; justify-content: space-between;
    padding: 4px 10px; font-size: 7.5pt; border-bottom: 1px solid #f0f2f6;
  }
  .totals-line.deduction { color: #b00; }
  .totals-divider { border-top: 2px solid #171363; }
  .credit-line span:last-child { color: #05998d; font-weight: 700; }
  .totals-credit-note {
    margin-top: 6px; padding: 5px 7px;
    border: 1px solid #05998d; border-radius: 3px;
    background: #f0fbfa; color: #05998d;
    font-size: 7pt; line-height: 1.35;
  }
  .totals-final {
    display: flex; justify-content: space-between;
    padding: 5px 10px; font-size: 9pt; font-weight: 700;
    background: #eef2f8;
  }
  .totals-final span { color: #171363; }

  /* ── Footer ── */
  .stmt-footer {
    font-size: 7pt; color: #666; line-height: 1.5;
    padding-top: 6px; border-top: 1px solid #e0e4ed; margin-bottom: 6px;
  }

  /* ── Tear strip ── */
  .tear-strip {
    border-top: 2px dashed #aaa; text-align: center;
    position: relative; margin: 10px 0 8px;
  }
  .tear-label {
    display: inline-block; background: #fff; padding: 0 10px;
    position: relative; top: -9px;
    font-size: 7pt; color: #999; text-transform: uppercase; letter-spacing: 0.8px;
  }

  /* ── Payment stub ── */
  .stub {
    border: 1px solid #c8d4e8; border-radius: 5px;
    padding: 9px 12px; page-break-inside: avoid;
  }
  .stub-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    margin-bottom: 7px;
  }
  .stub-practice-name { font-size: 8.5pt; font-weight: 700; color: #171363; }
  .stub-practice-addr { font-size: 7pt; color: #666; line-height: 1.4; margin-top: 2px; }
  .stub-title { font-size: 9pt; font-weight: 800; color: #171363; text-align: right; }

  .stub-fields {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px 10px;
    margin-bottom: 8px;
  }
  .stub-field { display: flex; flex-direction: column; gap: 2px; }
  .stub-field-lbl { font-size: 6.5pt; text-transform: uppercase; letter-spacing: 0.4px; color: #999; }
  .stub-field-val {
    font-size: 8pt; font-weight: 600; color: #1a1a2e;
    border-bottom: 1px solid #c8d4e8; padding-bottom: 2px; min-height: 13px;
  }
  .stub-field-val.amount { font-size: 10pt; font-weight: 800; color: #171363; }
  .stub-field-val.blank { color: transparent; }

  .stub-payment { border-top: 1px solid #e0e4ed; padding-top: 7px; }
  .stub-payment-lbl { font-size: 6.5pt; text-transform: uppercase; letter-spacing: 0.4px; color: #999; margin-bottom: 5px; }
  .stub-methods { display: flex; gap: 12px; font-size: 7.5pt; margin-bottom: 6px; flex-wrap: wrap; }
  .stub-method { display: flex; align-items: center; gap: 4px; }
  .stub-checkbox {
    width: 10px; height: 10px; border: 1px solid #aaa;
    border-radius: 2px; display: inline-block; flex-shrink: 0;
  }
  .stub-card-grid {
    display: grid; grid-template-columns: 3fr 1.2fr 1fr; gap: 6px; margin-bottom: 5px;
  }
  .stub-input { display: flex; flex-direction: column; gap: 2px; }
  .stub-input-lbl { font-size: 6.5pt; text-transform: uppercase; letter-spacing: 0.3px; color: #999; }
  .stub-input-line { border-bottom: 1px solid #aaa; height: 12px; }
  .stub-sig-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 6px; }
`;

// ─── Main export ─────────────────────────────────────────────────────────────
// statementNumber is intentionally omitted from this function: the DB record is
// created only after the user actually prints (afterprint event), so the number
// is not known at document-generation time. The account number serves as the
// patient-facing reference on the printed document.

// Builds the inner statement document (everything that lives inside #page) for a
// single patient and returns it as an HTML string. The document shell (doctype,
// shared CSS, toolbar, afterprint bridge) is added by openStatementWindow, so the
// same body can be printed on its own or concatenated into a bulk print job.
function buildStatementBody({ patient, facility, claims, statementDate, creditSummary }) {
  const today = statementDate || new Date().toISOString().slice(0, 10);
  const dueDate = addDays(today, 30);

  // Credit sitting on the account but not yet applied to anything.
  //
  // Shown, never subtracted. Deducting it here would print a balance the ledger does
  // not agree with — the money is still posted against the claim it was overpaid on
  // until someone applies it, and it may be owed back as a refund rather than spent.
  // The patient sees that we know about it; the books stay true.
  const acctCredit = Math.max(0, parseFloat(creditSummary?.unapplied_credit) || 0);

  // Totals across selected claims
  let totalCharges = 0, totalInsurance = 0, totalAdj = 0, totalPtResp = 0, totalPtPaid = 0;
  claims.forEach((c) => {
    totalCharges   += parseFloat(c.total_charge)           || 0;
    totalInsurance += parseFloat(c.insurance_payments)     || 0;
    totalAdj       += parseFloat(c.adjustments)            || 0;
    totalPtResp    += parseFloat(c.patient_responsibility) || 0;
    totalPtPaid    += parseFloat(c.patient_paid)           || 0;
  });

  // Service date range across claims
  const claimDates = claims
    .map((c) => parseLocalDate(c.service_date))
    .filter((d) => !isNaN(d));
  const minDate = claimDates.length ? new Date(Math.min(...claimDates)) : null;
  const maxDate = claimDates.length ? new Date(Math.max(...claimDates)) : null;
  const serviceDateRange = !minDate ? '—'
    : minDate.getTime() === maxDate.getTime()
      ? fmtDate(minDate.toISOString())
      : `${fmtShort(minDate.toISOString())} – ${fmtShort(maxDate.toISOString())}`;

  // Unique rendering/referring providers
  const renderingProviders = [...new Set(
    claims.map((c) => v(c.rendering_provider)).filter(Boolean)
  )];
  const referringProviders = [...new Set(
    claims.map((c) => v(c.referring_provider)).filter(Boolean)
  )];
  const providerStr = renderingProviders.join(', ') || '—';
  const referringStr = referringProviders.length ? referringProviders.join(', ') : '—';

  const acctNum = `#${String(patient.id || patient.patient_id).padStart(5, '0')}`;
  const patientName = `${v(patient.first_name)} ${v(patient.last_name)}`.trim();
  const patientAddr = [patient.street_address, patient.city, patient.state, patient.zip]
    .filter(Boolean).join(', ');
  const facAddr = [facility.street_address, facility.city, facility.state, facility.zip]
    .filter(Boolean).join(', ');

  // ── Build one block per claim ──────────────────────────────────────────────
  const claimBlocks = claims.map((claim) => {
    const diagStr = (claim.diagnoses || [])
      .map((d) => v(d.icd_code)).filter(Boolean).join(', ') || '—';
    const billedTo    = claim.remits?.[0]?.payer_name || 'Patient';
    const insPayment  = parseFloat(claim.insurance_payments)     || 0;
    const adjustments = parseFloat(claim.adjustments)            || 0;
    const ptResp      = parseFloat(claim.patient_responsibility) || 0;
    const ptPaid      = parseFloat(claim.patient_paid)           || 0;
    const charges     = parseFloat(claim.total_charge)           || 0;
    const balance     = Math.max(0, ptResp - ptPaid);

    // Financial cells shown on the last (or only) service line row
    const financialCells = `
      <td class="r">${fmtMoney(insPayment)}</td>
      <td class="r">${fmtMoney(adjustments)}</td>
      <td class="r">${ptPaid > 0 ? fmtMoney(ptPaid) : '$0.00'}</td>
      <td class="r balance-val">${fmtMoney(balance)}</td>
      <td class="billed-to-val">${billedTo}</td>`;
    const dimCells = `
      <td class="r dim-cell">&mdash;</td>
      <td class="r dim-cell">&mdash;</td>
      <td class="r dim-cell">&mdash;</td>
      <td class="r dim-cell">&mdash;</td>
      <td class="dim-cell">&mdash;</td>`;

    let lineRows = '';
    if (claim.service_lines?.length) {
      const lines = claim.service_lines;
      lineRows = lines.map((sl, idx) => {
        const isLast  = idx === lines.length - 1;
        const isMulti = lines.length > 1;
        const dateFrom = fmtShort(sl.service_date_from);
        const dateTo = sl.service_date_to && sl.service_date_to !== sl.service_date_from
          ? ` &ndash; ${fmtShort(sl.service_date_to)}` : '';
        return `
          <tr${isLast && isMulti ? ' class="claim-totals-row"' : ''}>
            <td>${dateFrom}${dateTo}</td>
            <td>${v(claim.rendering_provider)}</td>
            <td><span class="cpt-pill">${v(sl.cpt_hcpcs_code)}</span></td>
            <td class="c">${v(sl.units) || '1'}</td>
            <td>${diagStr}</td>
            <td class="r">${fmtMoney(sl.charges)}</td>
            ${isLast ? financialCells : dimCells}
          </tr>`;
      }).join('');
    } else {
      lineRows = `
        <tr>
          <td>${fmtShort(claim.service_date)}</td>
          <td>${v(claim.rendering_provider)}</td>
          <td>&mdash;</td>
          <td class="c">&mdash;</td>
          <td>${diagStr}</td>
          <td class="r">${fmtMoney(charges)}</td>
          ${financialCells}
        </tr>`;
    }

    // One <tbody> per claim inside a single shared table, so the column headers are
    // printed once for the whole statement instead of being repeated above every
    // claim. Grouping by tbody keeps each claim visually separate and lets the
    // browser avoid splitting a claim across a page break.
    return `
        <tbody class="claim-group">
          ${lineRows}
        </tbody>`;
  }).join('');

  // The <thead> is declared once; browsers repeat it automatically at the top of
  // each printed page, which is what a multi-page statement wants anyway.
  const claimTable = `
    <table class="detail-table">
      <colgroup>
        <col style="width:10%">
        <col style="width:13%">
        <col style="width:7%">
        <col style="width:4%">
        <col style="width:13%">
        <col style="width:9%">
        <col style="width:9%">
        <col style="width:9%">
        <col style="width:8%">
        <col style="width:9%">
        <col style="width:9%">
      </colgroup>
      <thead>
        <tr>
          <th>Svc Date</th>
          <th>Provider</th>
          <th>Proc.</th>
          <th class="c">Units</th>
          <th>Diagnoses</th>
          <th class="r">Charges</th>
          <th class="r">Ins. Paid</th>
          <th class="r">Adj.</th>
          <th class="r">Pt. Paid</th>
          <th class="r">Balance</th>
          <th>Billed To</th>
        </tr>
      </thead>
      ${claimBlocks}
    </table>`;

  return `

  <!-- ── Header ── -->
  <div class="stmt-header">
    <div>
      <div class="practice-name">${v(facility.name)}</div>
      <div class="practice-meta">
        ${v(facAddr)}<br>
        ${v(facility.phone) ? `Tel: ${v(facility.phone)}` : ''}${v(facility.fax) ? `&nbsp;&nbsp;|&nbsp;&nbsp;Fax: ${v(facility.fax)}` : ''}<br>
        Tax ID: ${v(facility.tax_id)}&nbsp;&nbsp;|&nbsp;&nbsp;NPI: ${v(facility.npi)}
      </div>
    </div>
    <div class="stmt-title-block">
      <div class="stmt-title">ITEMIZED STATEMENT</div>
      <div class="stmt-meta">
        Account #: <strong>${acctNum}</strong><br>
        Statement Date: ${fmtDate(today)}<br>
        Payment Due: ${fmtDate(dueDate)}
      </div>
    </div>
  </div>

  <!-- ── Amount due banner ── -->
  <div class="amount-due-bar">
    <div class="amount-due-left">
      <div class="amount-due-label">Total Amount Due</div>
      <div class="amount-due-sub">Please remit payment by ${fmtDate(dueDate)}</div>
    </div>
    <div class="amount-due-value">${fmtMoney(Math.max(0, totalPtResp - totalPtPaid))}</div>
  </div>

  <!-- ── Patient / Account info ── -->
  <!-- Patient Information box intentionally shows only Name and Address —
       Account Number and DOB are kept in Statement Details (right column)
       so they are not visible through the envelope window (HIPAA). -->
  <div class="info-grid">
    <div class="info-box">
      <div class="info-box-title">Patient Information</div>
      <div class="info-row-line">
        <span class="info-lbl">Patient Name</span>
        <span class="info-val">${patientName}</span>
      </div>
      <div class="info-row-line">
        <span class="info-lbl">Address</span>
        <span class="info-val">${v(patientAddr) || '&mdash;'}</span>
      </div>
    </div>
    <div class="info-box">
      <div class="info-box-title">Statement Details</div>
      <div class="info-row-line">
        <span class="info-lbl">Account Number</span>
        <span class="info-val">${acctNum}</span>
      </div>
      <div class="info-row-line">
        <span class="info-lbl">Date of Birth</span>
        <span class="info-val">${fmtDate(patient.date_of_birth)}</span>
      </div>
      <div class="info-row-line">
        <span class="info-lbl">Statement Date</span>
        <span class="info-val">${fmtDate(today)}</span>
      </div>
      <div class="info-row-line">
        <span class="info-lbl">Service Date Range</span>
        <span class="info-val">${serviceDateRange}</span>
      </div>
      <div class="info-row-line">
        <span class="info-lbl">Rendering Provider</span>
        <span class="info-val">${providerStr}</span>
      </div>
      <div class="info-row-line">
        <span class="info-lbl">Referring Provider</span>
        <span class="info-val">${referringStr}</span>
      </div>
    </div>
  </div>

  <!-- ── Account detail ── -->
  <div class="section-heading">Account Detail</div>
  ${claimTable}

  <!-- ── Statement totals ── -->
  <div class="totals-wrapper">
    <div class="totals-box">
      <div class="totals-box-title">Statement Totals</div>
      <div class="totals-line">
        <span>Total Charges</span>
        <span>${fmtMoney(totalCharges)}</span>
      </div>
      <div class="totals-line deduction">
        <span>Insurance Payments</span>
        <span>&minus;${fmtMoney(totalInsurance)}</span>
      </div>
      <div class="totals-line deduction">
        <span>Adjustments</span>
        <span>&minus;${fmtMoney(totalAdj)}</span>
      </div>
      <div class="totals-line deduction">
        <span>Patient Payments</span>
        <span>&minus;${fmtMoney(totalPtPaid)}</span>
      </div>
      ${acctCredit > 0 ? `
      <div class="totals-line credit-line">
        <span>Credit on account</span>
        <span>${fmtMoney(acctCredit)}</span>
      </div>` : ''}
      <div class="totals-divider"></div>
      <div class="totals-final">
        <span>Balance Due</span>
        <span>${fmtMoney(Math.max(0, totalPtResp - totalPtPaid))}</span>
      </div>
      ${acctCredit > 0 ? `
      <div class="totals-credit-note">
        Your account holds a credit of ${fmtMoney(acctCredit)} that has not been applied to the
        balance above. Please contact our billing office &mdash; it may be applied to these
        charges or refunded to you.
      </div>` : ''}
    </div>
  </div>

  <!-- ── Footer ── -->
  <div class="stmt-footer">
    <strong>Questions about your statement?</strong> Please contact our billing office at
    ${v(facility.phone) || 'the number listed above'}.
    Reference account number <strong>${acctNum}</strong> when calling.
    &nbsp;&bull;&nbsp;
    Please detach and return the payment stub below with your remittance.
    Thank you for choosing <strong>${v(facility.name)}</strong>.
  </div>

  <!-- ── Tear strip ── -->
  <div class="tear-strip">
    <span class="tear-label">&#9986;&nbsp; Please detach here and return with your payment &nbsp;&#9986;</span>
  </div>

  <!-- ── Payment stub ── -->
  <div class="stub">
    <div class="stub-header">
      <div>
        <div class="stub-practice-name">${v(facility.name)}</div>
        <div class="stub-practice-addr">${v(facAddr)}<br>${v(facility.phone)}</div>
      </div>
      <div class="stub-title">PAYMENT STUB</div>
    </div>

    <div class="stub-fields">
      <div class="stub-field">
        <span class="stub-field-lbl">Patient Name</span>
        <span class="stub-field-val">${patientName}</span>
      </div>
      <div class="stub-field">
        <span class="stub-field-lbl">Account Number</span>
        <span class="stub-field-val">${acctNum}</span>
      </div>
      <div class="stub-field">
        <span class="stub-field-lbl">Statement Date</span>
        <span class="stub-field-val">${fmtDate(today)}</span>
      </div>
      <div class="stub-field">
        <span class="stub-field-lbl">Amount Due</span>
        <span class="stub-field-val amount">${fmtMoney(Math.max(0, totalPtResp - totalPtPaid))}</span>
      </div>
      <div class="stub-field">
        <span class="stub-field-lbl">Due Date</span>
        <span class="stub-field-val">${fmtDate(dueDate)}</span>
      </div>
      <div class="stub-field">
        <span class="stub-field-lbl">Amount Enclosed</span>
        <span class="stub-field-val blank">$&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
      </div>
    </div>

    <div class="stub-payment">
      <div class="stub-payment-lbl">Payment Method</div>
      <div class="stub-methods">
        <label class="stub-method"><span class="stub-checkbox"></span> Check</label>
        <label class="stub-method"><span class="stub-checkbox"></span> Visa</label>
        <label class="stub-method"><span class="stub-checkbox"></span> Mastercard</label>
        <label class="stub-method"><span class="stub-checkbox"></span> Discover</label>
        <label class="stub-method"><span class="stub-checkbox"></span> AmEx</label>
      </div>
      <div class="stub-card-grid">
        <div class="stub-input">
          <span class="stub-input-lbl">Card Number</span>
          <div class="stub-input-line"></div>
        </div>
        <div class="stub-input">
          <span class="stub-input-lbl">Expiration (MM/YY)</span>
          <div class="stub-input-line"></div>
        </div>
        <div class="stub-input">
          <span class="stub-input-lbl">CVV</span>
          <div class="stub-input-line"></div>
        </div>
      </div>
      <div class="stub-sig-grid">
        <div class="stub-input">
          <span class="stub-input-lbl">Cardholder Signature</span>
          <div class="stub-input-line"></div>
        </div>
        <div class="stub-input">
          <span class="stub-input-lbl">Date</span>
          <div class="stub-input-line"></div>
        </div>
      </div>
    </div>
  </div>

`;
}

// ─── Document shell + entry points ────────────────────────────────────────────

// Wraps one or more statement bodies in the shared print document shell (CSS,
// a screen-only toolbar, and an afterprint bridge that posts back to the opener)
// and opens it in a new window.
/* eslint-disable no-useless-escape -- the `</script>` below closes the generated document's inline script; unescaping it would terminate the script block early. */
function openStatementWindow({ title, toolbarLabel, innerHtml, afterPrintType }) {
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>${STATEMENT_CSS}</style>
  <script>
    window.addEventListener('afterprint', function() {
      try {
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({ type: '${afterPrintType}' }, '*');
        }
      } catch (e) {}
    }, { once: true });
  <\/script>
</head>
<body>

<div id="toolbar">
  <button class="pbtn" onclick="window.print()">&#128438;&nbsp; Print</button>
  <button class="cbtn" onclick="window.close()">Close</button>
  <span style="font-size:9pt;color:#555;margin-left:10px;">${toolbarLabel}</span>
</div>

${innerHtml}
</body>
</html>`;

  return openPrintWindow(html);
}
/* eslint-enable no-useless-escape */

// Single-patient statement. Posts __STMT_AFTER_PRINT__ on print so the caller
// can log exactly one statement to history.
export function generateStatement({ patient, facility, claims, statementDate, creditSummary }) {
  const acctNum     = `#${String(patient.id || patient.patient_id).padStart(5, '0')}`;
  const patientName = `${v(patient.first_name)} ${v(patient.last_name)}`.trim();
  return openStatementWindow({
    title:          `Patient Statement — ${patientName}`,
    toolbarLabel:   `Patient Statement &mdash; ${acctNum} &mdash; ${patientName}`,
    innerHtml:      `<div id="page">${buildStatementBody({ patient, facility, claims, statementDate, creditSummary })}</div>`,
    afterPrintType: '__STMT_AFTER_PRINT__',
  });
}

// Bulk statements — one print job containing every selected patient's statement,
// each forced onto its own sheet. Posts __STMT_BULK_AFTER_PRINT__ on print so the
// caller can log all of them at once. `items` is [{ patient, facility, claims }].
export function generateBulkStatements(items, { statementDate } = {}) {
  const list = Array.isArray(items) ? items : [];
  const sheets = list.map(({ patient, facility, claims, credit_summary }) =>
    `<div class="statement-sheet">${buildStatementBody({ patient, facility, claims, statementDate, creditSummary: credit_summary })}</div>`
  ).join('');
  return openStatementWindow({
    title:          `Patient Statements (${list.length})`,
    toolbarLabel:   `${list.length} Patient Statement${list.length !== 1 ? 's' : ''} &mdash; single print job`,
    innerHtml:      `<div id="page">${sheets}</div>`,
    afterPrintType: '__STMT_BULK_AFTER_PRINT__',
  });
}
